import clsx from 'clsx';
import { MouseEventHandler, ReactNode } from 'react';
import { ColorMap } from '../types';

// text-gray-700 text-gray-800 text-gray-100 text-blue-700 text-blue-100 text-green-100 text-red-100
const getTextColor = (color: string): string => {
  const [colorName, intensity] = color.split('-');
  return ['100', '200', '300'].includes(intensity)
    ? intensity === '100'
      ? `text-${colorName}-700`
      : `text-${colorName}-800`
    : `text-${colorName}-100`;
};
// Simplified color mapping
const colorMap: Record<string, ColorMap> = {
  'gray-100': { bg: 'bg-gray-100', hover: 'hover:bg-gray-200' },
  'gray-300': { bg: 'bg-gray-300', hover: 'hover:bg-gray-400' },
  'gray-500': { bg: 'bg-gray-500', hover: 'hover:bg-gray-600' },
  'blue-100': { bg: 'bg-blue-100', hover: 'hover:bg-blue-200' },
  'blue-500': { bg: 'bg-blue-500', hover: 'hover:bg-blue-600' },
  'green-500': { bg: 'bg-green-500', hover: 'hover:bg-green-600' },
  'red-500': { bg: 'bg-red-500', hover: 'hover:bg-red-600' },
  'orange-300': { bg: 'bg-orange-300', hover: 'hover:bg-orange-400' },
  'sage-500': {
    bg: 'bg-green-100 text-green-800 dark:bg-green-800/30 dark:text-green-600',
    hover: 'dark:hover:bg-green-800/50 hover:bg-green-200',
  },
  'sage-400': {
    bg: 'bg-onlight-bg-1 text-green-800 dark:bg-dark-bg dark:text-green-600',
    hover: 'dark:hover:bg-green-800/50 hover:bg-green-100',
  },
  'earth-500': {
    bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-600',
    hover: 'dark:hover:bg-yellow-800/50',
  },
  'earth-300': { bg: 'bg-earth-300', hover: 'hover:bg-earth-400' },
} as const;

type ColorKey = keyof typeof colorMap;

interface ToggleButtonProps {
  // Core functionality
  isActive: boolean;
  text: string;
  onToggle?: (isActive: boolean) => void;

  // Styling
  activeColor: ColorKey; // Predefined Tailwind color keys
  inactiveColor: ColorKey; // Predefined Tailwind color keys

  // Variants
  size?: 'small' | 'medium' | 'large';

  // Standard props
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  dataTestid?: string;
  title?: string;
}

export function ToggleButton(props: ToggleButtonProps) {
  const {
    isActive,
    onToggle,
    activeColor,
    inactiveColor,
    size = 'medium',
    children,
    disabled = false,
    className,
    dataTestid,
    title,
    text,
  } = props;

  const handleClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (!disabled) {
      onToggle?.(!isActive);
    }
  };

  // Get the color classes from the map
  const currentColor = isActive ? activeColor : inactiveColor;
  const colorClasses = colorMap[currentColor];

  return (
    <button
      type="button"
      title={title}
      className={clsx(
        // Base styles
        'focus-visible:ring-primary-500 dark:ring-offset-dark-bg inline-flex items-center justify-center border border-transparent font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',

        // Shape - rectangle with rounded corners
        'rounded-md',

        // Size variants
        size === 'small' && 'px-3 py-1',
        size === 'medium' && 'px-4 py-2',
        size === 'large' && 'px-6 py-3',

        // Colors from map
        colorClasses?.bg,
        colorClasses?.opacity ?? '',
        !disabled && colorClasses?.hover,

        // Disabled state
        disabled && 'cursor-not-allowed',

        // Animations
        'transition-all duration-200 ease-in-out',

        // Transform on active for tactile feedback
        !disabled && 'active:scale-95',

        // Custom className
        className || ''
      )}
      disabled={disabled}
      onClick={handleClick}
      data-testid={dataTestid}
      role="switch"
      aria-checked={isActive}
      aria-disabled={disabled}
    >
      <span
        className={clsx(
          // Text size based on button size
          size === 'small' && 'text-xs',
          size === 'medium' && 'text-sm',
          size === 'large' && 'text-base',

          // Text color - variant of background color
          getTextColor(currentColor),

          // Font weight based on state
          isActive ? 'font-medium' : 'font-normal',

          // Ensure text doesn't wrap
          'whitespace-nowrap'
        )}
      >
        {text}
      </span>
      {children && children}
    </button>
  );
}
export default ToggleButton;
